import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface InvoiceReportItem {
  id: string;
  invoice_number: string;
  patient_name: string;
  patient_unique_id: string;
  issue_date: string;
  due_date: string;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  status: string;
  reference_type?: string;
}

export default function InvoiceReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<InvoiceReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [referenceTypeFilter, setReferenceTypeFilter] = useState<string>('all');

  useEffect(() => {
    loadInvoiceReport();
  }, [startDate, endDate, statusFilter, referenceTypeFilter]);

  const loadInvoiceReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockInvoiceReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          issue_date,
          due_date,
          subtotal,
          discount_amount,
          tax_amount,
          total,
          amount_paid,
          balance_due,
          status,
          reference_type,
          patient:patients(name, unique_id)
        `);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (referenceTypeFilter !== 'all') {
        query = query.eq('reference_type', referenceTypeFilter);
      }
      if (startDate) {
        query = query.gte('issue_date', startDate);
      }
      if (endDate) {
        query = query.lte('issue_date', endDate);
      }

      const { data, error } = await query.order('issue_date', { ascending: false });
      if (error) throw error;

      const formattedData: InvoiceReportItem[] = (data || []).map(item => ({
        id: item.id,
        invoice_number: item.invoice_number,
        patient_name: item.patient?.name || 'N/A',
        patient_unique_id: item.patient?.unique_id || 'N/A',
        issue_date: item.issue_date,
        due_date: item.due_date,
        subtotal: item.subtotal,
        discount_amount: item.discount_amount,
        tax_amount: item.tax_amount,
        total: item.total,
        amount_paid: item.amount_paid,
        balance_due: item.balance_due,
        status: item.status,
        reference_type: item.reference_type,
      }));

      setReportData(formattedData);
    } catch (error) {
      console.error('Error loading invoice report:', error);
      setReportData(getMockInvoiceReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockInvoiceReport = (): InvoiceReportItem[] => {
    return [
      {
        id: '1',
        invoice_number: 'INV-2025-001',
        patient_name: 'John Doe',
        patient_unique_id: 'P-001',
        issue_date: '2025-01-27',
        due_date: '2025-02-27',
        subtotal: 5000,
        discount_amount: 500,
        tax_amount: 0,
        total: 4500,
        amount_paid: 4500,
        balance_due: 0,
        status: 'paid',
        reference_type: 'opd',
      },
      {
        id: '2',
        invoice_number: 'INV-2025-002',
        patient_name: 'Jane Smith',
        patient_unique_id: 'P-002',
        issue_date: '2025-01-26',
        due_date: '2025-02-26',
        subtotal: 3000,
        discount_amount: 0,
        tax_amount: 0,
        total: 3000,
        amount_paid: 1000,
        balance_due: 2000,
        status: 'partial',
        reference_type: 'lab',
      },
      {
        id: '3',
        invoice_number: 'INV-2025-003',
        patient_name: 'Ahmed Khan',
        patient_unique_id: 'P-003',
        issue_date: '2025-01-25',
        due_date: '2025-02-25',
        subtotal: 8000,
        discount_amount: 800,
        tax_amount: 0,
        total: 7200,
        amount_paid: 7200,
        balance_due: 0,
        status: 'paid',
        reference_type: 'ipd',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_unique_id.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalRevenue = filteredData.reduce((sum, item) => sum + item.total, 0);
  const totalCollected = filteredData.reduce((sum, item) => sum + item.amount_paid, 0);
  const totalDiscount = filteredData.reduce((sum, item) => sum + item.discount_amount, 0);
  const totalDue = filteredData.reduce((sum, item) => sum + item.balance_due, 0);

  const handleExportCSV = () => {
    const headers = ['Invoice #', 'Patient ID', 'Patient Name', 'Issue Date', 'Due Date', 'Subtotal', 'Discount', 'Tax', 'Total', 'Paid', 'Balance Due', 'Status', 'Type'];
    const rows = filteredData.map(item => [
      item.invoice_number,
      item.patient_unique_id,
      item.patient_name,
      item.issue_date,
      item.due_date,
      item.subtotal,
      item.discount_amount,
      item.tax_amount,
      item.total,
      item.amount_paid,
      item.balance_due,
      item.status,
      item.reference_type || 'N/A',
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Invoice Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Invoices</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredData.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalRevenue)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Collected</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalCollected)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Discount</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(totalDiscount)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search invoice, patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div>
              <select
                value={referenceTypeFilter}
                onChange={(e) => setReferenceTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="opd">OPD</option>
                <option value="ipd">IPD</option>
                <option value="lab">Lab</option>
                <option value="radiology">Radiology</option>
                <option value="pharmacy">Pharmacy</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{item.patient_name}</div>
                          <div className="text-gray-500 text-xs">{item.patient_unique_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.issue_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(item.subtotal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                        {formatCurrency(item.discount_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(item.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {formatCurrency(item.amount_paid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                        {formatCurrency(item.balance_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            item.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : item.status === 'partial'
                              ? 'bg-yellow-100 text-yellow-800'
                              : item.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
